import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { devRequest, devSession, publicGet, publicPost, setDevSession } from "../admin/api";
import { loadTurnstile, turnstileTheme } from "../lib/turnstile";
import PrivacyNotice from "../components/brand/PrivacyNotice";

// Sign-in form shared by /get-key and /watchlist: email -> one-time link (no passwords).
export function SignIn({ next = "key" }) {
  const [cfg, setCfg] = useState(null);
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [token, setToken] = useState("");
  const [sent, setSent] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const widget = useRef(null);
  const widgetId = useRef(null);

  useEffect(() => { publicGet("/public/dev/config").then(setCfg).catch((ex) => setErr(ex.message)); }, []);
  useEffect(() => {
    if (!cfg || !cfg.turnstile_enabled || !cfg.turnstile_site_key) return undefined;
    let cancelled = false;
    loadTurnstile().then((ts) => {
      if (cancelled || !widget.current) return;
      widgetId.current = ts.render(widget.current, {
        sitekey: cfg.turnstile_site_key, theme: turnstileTheme(),
        callback: (t) => setToken(t), "expired-callback": () => setToken(""), "error-callback": () => setToken(""),
      });
    }).catch((ex) => setErr(ex.message));
    return () => { cancelled = true; };
  }, [cfg]);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      setSent(await publicPost("/public/dev/magic-link", { email: email.trim(), turnstile_token: token, website, next }));
    } catch (ex) {
      setErr(ex.message);
      if (window.turnstile && widgetId.current != null) { window.turnstile.reset(widgetId.current); setToken(""); }
    } finally { setBusy(false); }
  };

  if (sent) {
    return (
      <div className="hs-card max-w-2xl space-y-2" role="status" data-testid="signin-sent">
        <p><span className="hs-badge hs-badge-safe mr-2">Check your inbox</span>
          If the address can receive email, a sign-in link is on its way. It works once and expires in {sent.minutes} minutes.</p>
        {sent.dev_link && <p className="text-sm text-muted">Development mode (no email provider): <a className="hs-link" href={sent.dev_link} data-testid="dev-link">open the sign-in link</a></p>}
      </div>
    );
  }
  const needsToken = !!cfg?.turnstile_enabled && !token;
  return (
    <form onSubmit={submit} className="hs-card max-w-2xl space-y-4" data-testid="signin-form">
      <div>
        <label className="hs-label" htmlFor="dev-email">Email</label>
        <input id="dev-email" data-testid="dev-email" type="email" required maxLength={254} className="hs-input"
          value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
      </div>
      <div aria-hidden="true" style={{ position: "absolute", left: "-10000px" }}>
        <label htmlFor="dev-website">Website</label>
        <input id="dev-website" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
      </div>
      {cfg?.turnstile_enabled && <div ref={widget} data-testid="dev-turnstile" />}
      {err && <p role="alert" className="hs-badge hs-badge-danger">{err}</p>}
      <button type="submit" className="hs-btn hs-btn-primary" disabled={busy || needsToken} data-testid="dev-submit">
        {busy ? "Sending…" : "Email me a sign-in link"}</button>
      <PrivacyNotice />
    </form>
  );
}

// Exchanges ?token= from the email for a session, then removes it from the address bar.
export function useDevSession() {
  const [state, setState] = useState({ loading: true, me: null, err: "" });
  const load = useCallback(async () => {
    if (!devSession()) { setState({ loading: false, me: null, err: "" }); return; }
    try { setState({ loading: false, me: await devRequest("/public/dev/me"), err: "" }); }
    catch (ex) { setState({ loading: false, me: null, err: ex.status === 401 ? "" : ex.message }); }
  }, []);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tok = params.get("token");
    if (!tok) { load(); return; }
    window.history.replaceState(null, "", window.location.pathname);
    publicPost("/public/dev/session", { token: tok })
      .then((s) => { setDevSession(s.session); return load(); })
      .catch((ex) => setState({ loading: false, me: null, err: ex.message }));
  }, [load]);
  const signOut = async () => {
    try { await devRequest("/public/dev/logout", { method: "POST" }); } catch (_) { /* already gone */ }
    setDevSession(""); setState({ loading: false, me: null, err: "" });
  };
  return { ...state, reload: load, signOut };
}

export default function GetKey() {
  const { loading, me, err, reload, signOut } = useDevSession();
  const [fresh, setFresh] = useState(null);
  const [name, setName] = useState("CLI");
  const [msg, setMsg] = useState("");

  const issue = async (e) => {
    e.preventDefault(); setMsg("");
    try { setFresh(await devRequest("/public/dev/keys", { method: "POST", body: { name } })); reload(); }
    catch (ex) { setMsg(ex.message); }
  };
  const revoke = async (id) => {
    if (!window.confirm("Revoke this key? Anything using it stops working.")) return;
    try { await devRequest(`/public/dev/keys/${id}/revoke`, { method: "POST" }); reload(); }
    catch (ex) { setMsg(ex.message); }
  };

  return (
    <section data-testid="get-key-page" className="space-y-6">
      <h1 className="hs-display text-3xl md:text-4xl">Free developer key</h1>
      <p className="text-muted hs-prose">
        The <code>herdsafe</code> CLI and the lookup API work without an account (100 lookups a day). A free key raises
        that to 10,000 a month. Sign in with your email: there is no password.
      </p>
      {err && <p role="alert" className="hs-badge hs-badge-danger">{err}</p>}
      {loading ? <p className="text-muted">Loading…</p> : !me ? <SignIn next="key" /> : (
        <div className="space-y-4">
          <div className="hs-card max-w-2xl" data-testid="dev-account">
            Signed in as <strong>{me.user.email}</strong>.{" "}
            <Link className="hs-link" to="/watchlist">Your watchlist</Link> ({me.watch_count}) ·{" "}
            <button type="button" className="hs-link" onClick={signOut} data-testid="dev-signout">Sign out</button>
          </div>
          {fresh && (
            <div className="hs-card max-w-2xl space-y-2" role="status" data-testid="fresh-key">
              <p><strong>Your new key</strong> (copy it now: we only keep a hash, so it cannot be shown again):</p>
              <code className="block break-all" data-testid="fresh-key-value">{fresh.key}</code>
              <p className="text-sm text-muted">Use it with <code>HERDSAFE_API_KEY={"<key>"} npx herdsafe scan</code> or
                as <code>Authorization: Bearer {"<key>"}</code> on the API.</p>
            </div>
          )}
          <form onSubmit={issue} className="hs-card max-w-2xl space-y-3" data-testid="issue-form">
            <label className="hs-label" htmlFor="key-name">Key name</label>
            <input id="key-name" className="hs-input" maxLength={80} value={name} onChange={(e) => setName(e.target.value)} />
            <button type="submit" className="hs-btn hs-btn-primary" data-testid="issue-key">Create key</button>
            {msg && <p role="alert" className="hs-badge hs-badge-danger">{msg}</p>}
          </form>
          <div className="hs-card max-w-2xl">
            <h2 className="hs-display text-xl">Your keys</h2>
            {me.keys.length === 0 ? <p className="text-muted">No keys yet.</p> : (
              <ul className="space-y-2" data-testid="key-list">
                {me.keys.map((k) => (
                  <li key={k.id} className="flex flex-wrap items-center gap-3">
                    <span>{k.name}</span><code>…{k.hint}</code>
                    <span className="text-sm text-muted">{k.used_this_month} / {k.monthly_quota} this month</span>
                    {k.revoked_at ? <span className="hs-badge hs-badge-neutral">Revoked</span>
                      : <button type="button" className="hs-btn" onClick={() => revoke(k.id)}>Revoke</button>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
